import React from 'react';
import { SpaceBetween, Box, Button, ColumnLayout, Icon, IconProps } from '@amzn/awsui-components-core';
import './AttendeePathPicker.scss';

export type AttendeePath = 'upload' | 'clone' | 'manual';

interface AttendeePathPickerProps {
  onSelect: (path: AttendeePath) => void;
  disabledPaths?: Set<AttendeePath>;
  displayName: string;
}

interface AttendeeOption {
  path: AttendeePath;
  icon: IconProps['name'];
  title: string;
  description: string;
  actionLabel: string;
}

interface HuddleStat {
  emoji?: string;
  value: string;
  label: string;
}

const ATTENDEE_OPTIONS: readonly AttendeeOption[] = [
  {
    path: 'upload',
    icon: 'upload',
    title: 'Smart Upload',
    description: 'Drop any file and AI will extract attendees automatically.',
    actionLabel: 'Upload & Go'
  },
  {
    path: 'clone',
    icon: 'copy',
    title: 'Clone a Meeting',
    description: 'Pick a past meeting or paste a link to import attendees.',
    actionLabel: 'Browse & Import'
  },
  {
    path: 'manual',
    icon: 'add-plus',
    title: 'Add Manually',
    description: 'Search and add attendees one at a time.',
    actionLabel: 'Start Fresh'
  }
];

const STATS: readonly HuddleStat[] = [
  {
    emoji: '👥',
    value: '345+',
    label: 'Active Users'
  },
  {
    emoji: '📊',
    value: '1675+',
    label: 'Sessions'
  },
  {
    emoji: '🚀',
    value: '~12s',
    label: 'Avg Setup Time'
  },
  {
    emoji: '📅',
    value: '565+',
    label: 'Meetings Scheduled'
  }
];

function LandingHeader() {
  return (
    <Box padding={{ horizontal: 'xxl', top: 'xxxl', bottom: 's' }} textAlign="center">
      <SpaceBetween alignItems="center" size="xxs">
        <span className="huddle-landing-badge">⚡ AI-Powered Scheduling</span>
        <Box fontSize="display-l" fontWeight="heavy" variant="h1">
          Schedule Meetings 10x Faster
        </Box>
        <Box color="text-body-secondary" fontSize="body-m" variant="p">
          Stop playing email tag. Huddle+ finds the perfect time, and builds the invite for you effortlessly.
        </Box>
      </SpaceBetween>
    </Box>
  );
}

function AttendeeOptionsSection({
  displayName,
  onSelect,
  disabledPaths
}: {
  displayName: string;
  onSelect: (path: AttendeePath) => void;
  disabledPaths?: Set<AttendeePath>;
}) {
  return (
    <Box padding={{ bottom: 'xl', horizontal: 'xxl' }} textAlign="center">
      <SpaceBetween size="m">
        <SpaceBetween alignItems="center" size="xxxs">
          <Box fontSize="heading-m" fontWeight="bold" textAlign="center" variant="h3">
            👋 Hey {displayName}, let's set up the meeting for you.
          </Box>
          <Box color="text-body-secondary" fontSize="body-m" textAlign="center" variant="p">
            How would you like to add attendees?
          </Box>
        </SpaceBetween>
        <div className="attendee-options-grid">
          {ATTENDEE_OPTIONS.map((option) => {
            const isDisabled = disabledPaths?.has(option.path) ?? false;
            return (
              <button
                key={option.path}
                type="button"
                className={`path-card${isDisabled ? ' path-card--disabled' : ''}`}
                onClick={() => !isDisabled && onSelect(option.path)}
                disabled={isDisabled}
              >
                <SpaceBetween size="s" direction="vertical" alignItems="center">
                  <div className="path-card__icon">
                    <Icon name={option.icon} size="big" />
                  </div>
                  <Box fontSize="heading-l" textAlign="center" variant="strong">
                    {option.title}
                  </Box>
                  <Box color="text-body-secondary" textAlign="center" variant="p">
                    {option.description}
                  </Box>
                  <Button variant="primary" disabled={isDisabled}>
                    {option.actionLabel}
                  </Button>
                </SpaceBetween>
              </button>
            );
          })}
        </div>
      </SpaceBetween>
    </Box>
  );
}

// TODO: Add real-time stats for Huddle+ meetings, these stats valid as of April 2nd, 2026.
function StatsSection() {
  return (
    <Box padding={{ bottom: 'xl', horizontal: 'xxl' }}>
      <ColumnLayout borders="vertical" columns={4} variant="text-grid">
        {STATS.map((stat) => (
          <SpaceBetween key={stat.label} alignItems="center" size="xxxs">
            {stat.emoji && (
              <Box fontSize="heading-xl" textAlign="center" variant="p">
                {stat.emoji}
              </Box>
            )}
            <Box fontWeight="heavy" variant="awsui-key-label" fontSize="heading-xl">
              {stat.value}
            </Box>
            <Box color="text-body-secondary" variant="small">
              {stat.label}
            </Box>
          </SpaceBetween>
        ))}
      </ColumnLayout>
    </Box>
  );
}

export const AttendeePathPicker: React.FC<AttendeePathPickerProps> = ({ onSelect, disabledPaths, displayName }) => (
  <SpaceBetween size="xl">
    <div>
      <SpaceBetween size="xs">
        <LandingHeader />
        <AttendeeOptionsSection displayName={displayName} onSelect={onSelect} disabledPaths={disabledPaths} />
        <StatsSection />
      </SpaceBetween>
    </div>
  </SpaceBetween>
);
